import type { ReactNode } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useResource } from '@app/misc/useResource';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import {
    type MediaThumbnailInput,
    MediaResource,
    resourceDisplayName,
    resourceMediaKind,
    resourceThumbnailSrc,
} from './resourceMediaKind';

interface MediaContentProps {
    item: MediaResource;
    onMediaClick?: (item: MediaResource) => void;
}

interface MediaThumbnailFrameProps {
    item: MediaResource;
    thumbnailSrc: string | undefined;
    onMediaClick?: (item: MediaResource) => void;
    overlay?: ReactNode;
    placeholder?: ReactNode;
}

/** Thumbnail as in-flow img so masonry ImageList can size the tile; overlay on top. */
const MediaThumbnailFrame = ({
    item,
    thumbnailSrc,
    onMediaClick,
    overlay,
    placeholder,
}: MediaThumbnailFrameProps) => {
    const alt = item.alt || resourceDisplayName(item) || 'Media preview';

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                cursor: 'pointer',
                lineHeight: 0,
                '&:hover': { opacity: 0.9 },
            }}
            onClick={() => onMediaClick?.(item)}
        >
            {thumbnailSrc ? (
                <Box
                    component="img"
                    src={thumbnailSrc}
                    alt={alt}
                    sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.200',
                    }}
                >
                    {placeholder}
                </Box>
            )}
            {overlay ? (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        '& > *': { pointerEvents: 'auto' },
                    }}
                >
                    {overlay}
                </Box>
            ) : null}
        </Box>
    );
};

const MediaPlaceholderCard = ({
    item,
    onMediaClick,
    children,
}: {
    item: MediaResource;
    onMediaClick?: (item: MediaResource) => void;
    children: ReactNode;
}) => (
    <Box
        sx={{
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(45deg, #f5f5f5 30%, #e0e0e0 90%)',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
        }}
        onClick={() => onMediaClick?.(item)}
    >
        {children}
    </Box>
);

export const MediaContent = ({ item, onMediaClick }: MediaContentProps) => {
    const { data: src } = useResource(item.id);
    const { resourceTypeLabels } = useConfigurationContext();

    const kind = resourceMediaKind(item.type);
    const label = resourceTypeLabels.find(
        l => l.value.toLowerCase() === kind || l.value === item.type
    );
    if (!label) return null;

    const thumbnailSrc = resourceThumbnailSrc(
        item.thumbnail as MediaThumbnailInput
    );

    if (kind === 'image') {
        return (
            <img
                src={src ?? undefined}
                alt={item.alt || resourceDisplayName(item) || 'Media'}
                loading="lazy"
                style={{ cursor: 'pointer' }}
                onClick={() => onMediaClick?.(item)}
                width={'100%'}
                height={'100%'}
            />
        );
    }

    if (kind === 'video') {
        return (
            <MediaThumbnailFrame
                item={item}
                thumbnailSrc={thumbnailSrc}
                onMediaClick={onMediaClick}
                placeholder={
                    <IconButton
                        size="large"
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                        }}
                    >
                        <PlayArrowIcon fontSize="large" />
                    </IconButton>
                }
                overlay={
                    <IconButton
                        size="large"
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            },
                        }}
                    >
                        <PlayArrowIcon fontSize="large" />
                    </IconButton>
                }
            />
        );
    }

    if (kind === 'audio') {
        return (
            <Box sx={{ width: '100%' }}>
                <audio controls style={{ width: '100%' }}>
                    <source src={src ?? undefined} type="audio/mpeg" />
                    Your browser does not support the audio tag.
                </audio>
            </Box>
        );
    }

    if ((kind === 'document' || kind === 'other') && thumbnailSrc) {
        return (
            <MediaThumbnailFrame
                item={item}
                thumbnailSrc={thumbnailSrc}
                onMediaClick={onMediaClick}
            />
        );
    }

    return (
        <MediaPlaceholderCard item={item} onMediaClick={onMediaClick}>
            <label.icon />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                {resourceDisplayName(item) || `${label.label} file`}
            </Typography>
        </MediaPlaceholderCard>
    );
};
